import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Modal,
  StyleSheet,
  View,
  Text,
  Image,
  Dimensions,
  TouchableOpacity,
} from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { ActivityIndicator } from "react-native-paper";
import { AntDesign } from "@expo/vector-icons";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { headersStock } from "../../Reports/utils/stockHeaders";
import { validSignedURL } from "../../../utils/validateSignedUrl";
import { useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import { userClaimInformation } from "../../../models/usersModel";
import { getStocks } from "../../../services/stockService";
import Spinner from "../../../components/shared/Spinner";

interface ChinaTrackerItemCodeInfoModalProps {
  visible: boolean;
  onClose: any;
  itemCode: string;
}

const ChinaTrackerItemCodeInfoModal: React.FC<
  ChinaTrackerItemCodeInfoModalProps
> = ({ visible, onClose, itemCode }) => {
  const toast = useToast();
  const { isLargeDevice } = useContext(DeviceOrientationContext);
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );
  const [selectedImage, setSelectedImage] = useState<any>(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [stockItem, setStockItem] = useState<any>(null);

  const { stockReportList, isStockReportListLoaded } = useSelector(
    (state: any) => state.reports
  );
  const isMobileView = windowWidth < 768;

  useEffect(() => {
    const sub = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });
    return () => sub?.remove();
  }, []);
  const { userInfo } = useSelector((state: any) => state.configurations);

  useEffect(() => {
    if (!isStockReportListLoaded) {
      const _userInfo = userInfo as userClaimInformation;
      getStocks(_userInfo.team, _userInfo.zone_info, headersStock);
    } else {
      const item = stockReportList.find((s: any) => {
        return Array.isArray(s.ItemCode)
          ? s.ItemCode[0] === itemCode
          : s.ItemCode === itemCode;
      });

      if (item) {
        setStockItem(item);
        validSignedURL(itemCode, "label", setIsImageLoading, setSelectedImage);
      } else {
        toast.show("Item not found in stock report", { type: "danger" });
      }
    }
  }, [itemCode, stockReportList, isStockReportListLoaded]);

  const orderedKeys = [
    "ItmsGrpCod",
    "ItemName",
    "Location",
    "PerCtns",
    "PerPallet",
    "Available2",
    "AvaliablePallet",
    "OnWater",
    "OnOrder2",
  ];

  const specialLabels: any = stockItem
    ? {
        PerPallet: {
          label: "Total Per Pallet",
          value:
            stockItem.PerPallet && stockItem.PerCtns
              ? (stockItem.PerPallet * stockItem.PerCtns).toString()
              : "-",
          supportingValue:
            stockItem.PerPallet && stockItem.PerCtns
              ? stockItem.PerPallet.toString()
              : "-",
        },
        ItmsGrpCod: {
          label: "Display",
          value: "Per PIECE",
        },
        Available2: { label: "Available" },
        OnOrder2: { label: "On Order" },
      }
    : {};

  const detailItems = stockItem
    ? orderedKeys.map((key) => {
        if (specialLabels[key]) {
          const special = specialLabels[key];
          return {
            label: special.label,
            value: special.value || stockItem[key] || "-",
            supportingValue: special.supportingValue,
          };
        } else {
          return {
            label: headersStock[key]?.name || key,
            value: stockItem[key]?.toString() || "-",
          };
        }
      })
    : [];

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View
          style={
            isLargeDevice
              ? styles.modalContainer
              : styles.modalContainerFullScreen
          }
        >
          {/* Header */}
          <View style={styles.headerRow}>
            <TouchableOpacity onPress={onClose} style={styles.backButton}>
              <AntDesign name="arrowleft" size={22} color="#000" />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>Item Code: {itemCode}</Text>
          </View>

          {/* Scrollable Content */}
          <ScrollView
            contentContainerStyle={[
              styles.scrollContent,
              isMobileView && { paddingTop: 30 },
            ]}
          >
            <View
              style={[
                styles.contentContainer,
                isMobileView && styles.contentContainerMobile,
              ]}
            >
              {!isStockReportListLoaded || !stockItem ? (
                <View style={styles.spinnerContainer}>
                  <Spinner size="small" color="#74BBFF" />
                </View>
              ) : (
                <>
                  {/* Left - Product Details */}
                  <View
                    style={[
                      styles.detailsBox,
                      isMobileView && styles.detailsBoxMobile,
                    ]}
                  >
                    <Text style={styles.itemTitle}>{itemCode}</Text>
                    <Text style={styles.itemSubtitle}>
                      {stockItem.FrgnName}
                    </Text>

                    {detailItems.map((d, index) => (
                      <View key={index} style={styles.detailRow}>
                        <Text style={styles.label}>{d.label}</Text>
                        <Text style={styles.value}>
                          {d.value?.toLocaleString()}
                        </Text>
                        {d.supportingValue && (
                          <Text style={styles.supporting}>
                            ({d.supportingValue?.toLocaleString()})
                          </Text>
                        )}
                      </View>
                    ))}
                  </View>

                  {/* Divider */}
                  {!isMobileView && <View style={styles.verticalLine} />}

                  {/* Right - Product Image */}
                  <View
                    style={[
                      styles.imageBox,
                      isMobileView && styles.imageBoxMobile,
                    ]}
                  >
                    <View style={styles.imageContainer}>
                      {isImageLoading && (
                        <ActivityIndicator
                          size="large"
                          style={styles.loader}
                          color="#000"
                        />
                      )}
                      <Image
                        source={{ uri: selectedImage }}
                        style={[
                          styles.image,
                          isMobileView && { marginTop: 20 },
                        ]}
                        resizeMode="contain"
                      />
                    </View>
                  </View>
                </>
              )}
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    width: "80%",
    borderRadius: 10,
    maxHeight: "90%",
  },
  modalContainerFullScreen: {
    backgroundColor: "#fff",
    elevation: 8,
    height: "100%",
    width: "100%",
    padding: 10,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  backButton: { padding: 8, marginRight: 8 },
  headerTitle: {
    fontSize: 18,
    fontWeight: "700",
  },
  scrollContent: {
    flexGrow: 1,
  },
  contentContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  contentContainerMobile: {
    flexDirection: "column",
  },
  detailsBox: {
    flex: 1,
    padding: 12,
    maxWidth: "48%",
  },
  detailsBoxMobile: {
    maxWidth: "100%",
    marginBottom: 20,
  },
  imageBox: {
    flex: 1,
    padding: 12,
    maxWidth: "48%",
  },
  imageBoxMobile: {
    maxWidth: "100%",
    width: "100%",
    marginTop: 10,
    paddingTop: 10,
  },

  verticalLine: {
    width: 1,
    backgroundColor: "#ddd",
    marginHorizontal: 10,
  },
  itemTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 4,
  },
  itemSubtitle: {
    fontSize: 14,
    color: "#666",
    marginBottom: 10,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomColor: "#eee",
    borderBottomWidth: 1,
    paddingVertical: 8,
  },
  label: {
    flex: 1,
    fontWeight: "600",
    fontSize: 14,
  },
  value: {
    flex: 1,
    textAlign: "right",
    color: "#333",
    fontSize: 14,
  },
  supporting: {
    marginLeft: 8,
    color: "#888",
    fontSize: 12,
  },
  imageContainer: {
    minHeight: 200,
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    width: "100%",
    height: 250,
  },
  loader: {
    position: "absolute",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingTop: 15,
  },
  closeButton: {
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
  },
  spinnerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
});

export default ChinaTrackerItemCodeInfoModal;
